import React, { useEffect, useRef, useState } from "react";
import Resources from "../../../types/Resources";
import { CodeRole, CodeStatus, MethodOfWork, PermitCategory, PermitType, SignatureType } from "../../../common/enums";
import ParticipantsList, { ParticipantListItem } from "./ParticipantsList";
import { Accordion } from "react-bootstrap";
import Department from "../../../types/Department";
import {
    acceptGeneralDepartmentPermit,
    checkoutPermitAsync,
    createGeneralDepartmentPermit,
    createSignatureAsync,
    deleteSignatureAsync,
    finishGeneralDepartmentPermit,
    getDepartmentById,
    getGeneralDepartmentPermitByDocumentId,
    getGeneralDepartmentPermitBySequenceIdentifier,
    postRenewPermit,
    updateGeneralDepartmentPermit,
    updateGeneralDepartmentPermitParticipants,
    updateGeneralDepartmentPermitPositionActiveStatus,
    updatePermitAgreementEndTimeOnAcceptedGeneralDepartmentPermit
} from "../../../apiClient";
import Result from "../../../types/Result";
import { scrollToTop, showDangerDialog, showSuccessAlert, showValidationErrorDialog } from "../../../common";
import LoadingSpinner from "../../Common/LoadingSpinner";
import ProblemDetails from "../../../types/responses/ProblemDetails";
import GeneralDepartmentPermitRequest, {
    toCreateRequest,
    toUpdateRequest
} from "../../../types/requests/GeneralDepartmentPermitRequest";
import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import CreateSignatureRequest from "../../../types/requests/CreateSignatureRequest";
import Signature, { Signature_empty } from "../../../types/Signature";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import { getPermitIcon } from "../../../icons";
import FilePicker from "./FilePicker";
import UpdateParticipantsRequest from "../../../types/requests/UpdateParticipantsRequest";
import { fromParticipantListItem } from "../../../types/Person";
import GeneralDepartmentPermit from "../../../types/permits/GeneralDepartmentPermit";
import Measures from "../Conditions/Measures";
import ProtectiveEquipment from "../Conditions/ProtectiveEquipment";
import RiskIdentifications from "../Conditions/RiskIdentifications";
import GeneralDepartmentPermitFormState, {
    clearErrors,
    defaultFormState,
    populateErrorMessages,
    toFormState
} from "../../../types/forms/Permits/GeneralDepartmentPermitFormState";
import ProtectiveEquipmentFormState from "../../../types/forms/Permits/Conditions/ProtectiveEquipmentFormState";
import MeasuresFormState from "../../../types/forms/Permits/Conditions/MeasuresFormState";
import RiskIdentificationsFormState from "../../../types/forms/Permits/Conditions/RiskIdentificationsFormState";
import useSignature from "../../../hooks/useSignature";
import useRiskIdentifications from "../../../hooks/useRiskIdentificationsUIState";
import useMeasures from "../../../hooks/useMeasuesState";
import SignatureModal from "../../Signatures/SignatureModal";
import { PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import PositionPickerRow, { PositionPickerRow_toPosition } from "../../../types/forms/Permits/PositionPickerRow";
import TextInput from "../../Common/TextInput";
import RadioButton from "../../Common/RadioButton";
import TextArea from "../../Common/TextArea";
import ColumnCheckbox from "../../Common/ColumnCheckbox";
import LabelCheckbox from "../../Common/LabelCheckbox";
import PositionPicker from "./PositionPicker";

const hotWorkConditionsAccordionKey = "hotWorkConditions";
const workConditionsAccordionKey = "conditions";

export type GeneralDepartmentPermitFormProps = PermitFormProps & {
    canCreateUnderWaterWorkPermits: boolean;
    canCreateDivingPermits: boolean;
};

function GeneralDepartmentPermitForm(props: GeneralDepartmentPermitFormProps) {
    const [department, setDepartment] = useState<Department>();
    const [formState, setFormState] = useState<GeneralDepartmentPermitFormState>(defaultFormState());
    const [storedPermit, setStoredPermit] = useState<GeneralDepartmentPermit>();
    const [conditionsAccordionActiveKey, setConditionsAccordionActiveKey] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const riskIdentificationsUIState = useRiskIdentifications();
    const measuresUIState = useMeasures();

    const startDateRef = useRef<HTMLInputElement>(null);
    const endDateRef = useRef<HTMLInputElement>(null);
    const permitAgreementStartDateRef = useRef<HTMLInputElement>(null);
    const permitAgreementEndDateRef = useRef<HTMLInputElement>(null);

    async function signatureSaveHandler(
        signatureType: SignatureType,
        updatedProperty: keyof GeneralDepartmentPermitFormState,
        signatureBlob: Blob,
        nameClarification: string
    ) {
        if (!storedPermit) {
            console.error("Tried to save a signature with no stored permit in state.");
            return;
        }

        const request: CreateSignatureRequest = {
            signatureBlob,
            nameClarification,
            signatureType,
            workPermitId: storedPermit.entityId
        };

        const createResult: Result<number, string> = await createSignatureAsync(request);

        if (createResult.failed) {
            console.error(createResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("SaveSignatureError"));
            return;
        }

        const createdSignatureId: number = createResult.value;

        const newSignature: Signature = {
            id: createdSignatureId,
            canCreate: true,
            canRemove: true,
            createdAt: new Date(),
            nameClarification: nameClarification,
            signatureType: signatureType,
            base64Signature: ""
        };

        setFormState((prevValue) => ({
            ...prevValue,
            [updatedProperty]: newSignature
        }));

        refreshStoredPermit();
    }

    async function signatureRemoveHandler(
        updatedProperty: keyof GeneralDepartmentPermitFormState,
        signature: Signature
    ) {
        const deleteResult = await deleteSignatureAsync(signature);

        if (deleteResult.failed) {
            console.error(deleteResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("DeleteSignatureError"));
            return;
        }

        const newSignature: Signature = {
            canCreate: true,
            canRemove: true,
            id: 0,
            nameClarification: "",
            signatureType: signature.signatureType,
            createdAt: new Date(),
            base64Signature: ""
        };

        setFormState((prevValue) => ({
            ...prevValue,
            [updatedProperty]: newSignature
        }));

        refreshStoredPermit();
    }

    const permitAgreementSeekerSignature = storedPermit
        ? storedPermit.permitAgreementSeekerSignature
        : Signature_empty(SignatureType.PermitAgreementPermitSeeker);

    const permitAgreementProviderSignature = storedPermit
        ? storedPermit.permitAgreementProviderSignature
        : Signature_empty(SignatureType.PermitAgreementPermitProvider);

    const isFinishedPermitProviderSignature = storedPermit
        ? storedPermit.isFinishedPermitProviderSignature
        : Signature_empty(SignatureType.IsFinishedPermitProvider);

    const isFinishedPermitSeekerSignature = storedPermit
        ? storedPermit.isFinishedPermitSeekerSignature
        : Signature_empty(SignatureType.IsFinishedPermitSeeker);

    const permitAgreementSeekerSignatureManager = useSignature({
        onRemoveSignature: () =>
            signatureRemoveHandler("permitAgreementSeekerSignature", permitAgreementSeekerSignature),
        onSaveSignature: (signatureBlob, nameClarification) =>
            signatureSaveHandler(
                SignatureType.PermitAgreementPermitSeeker,
                "permitAgreementSeekerSignature",
                signatureBlob,
                nameClarification
            ),
        resources: props.resources,
        signature: permitAgreementSeekerSignature,
        baseUserInfo: props.baseUserInfo
    });

    const permitAgreementProviderSignatureManager = useSignature({
        onRemoveSignature: () =>
            signatureRemoveHandler("permitAgreementProviderSignature", permitAgreementProviderSignature),
        onSaveSignature: (signatureBlob, nameClarification) =>
            signatureSaveHandler(
                SignatureType.PermitAgreementPermitProvider,
                "permitAgreementProviderSignature",
                signatureBlob,
                nameClarification
            ),
        resources: props.resources,
        signature: permitAgreementProviderSignature,
        baseUserInfo: props.baseUserInfo
    });

    const isFinishedPermitProviderSignatureManager = useSignature({
        onRemoveSignature: () =>
            signatureRemoveHandler("isFinishedPermitProviderSignature", isFinishedPermitProviderSignature),
        onSaveSignature: (signatureBlob, nameClarification) =>
            isFinishedPermitProviderSignatureSaveHandler(signatureBlob, nameClarification),
        resources: props.resources,
        signature: isFinishedPermitProviderSignature,
        baseUserInfo: props.baseUserInfo
    });

    const isFinishedPermitSeekerSignatureManager = useSignature({
        onRemoveSignature: () =>
            signatureRemoveHandler("isFinishedPermitSeekerSignature", isFinishedPermitSeekerSignature),
        onSaveSignature: (signatureBlob, nameClarification) =>
            isFinishedPermitSeekerSignatureSaveHandler(signatureBlob, nameClarification),
        resources: props.resources,
        signature: isFinishedPermitSeekerSignature,
        baseUserInfo: props.baseUserInfo
    });

    useEffect(() => {
        async function fetchData() {
            const promises: Promise<any>[] = [getDepartmentById(props.departmentId)];

            if (props.documentReferenceId) {
                promises.push(getGeneralDepartmentPermitByDocumentId(props.documentReferenceId));
            } else if (props.sequenceIdentifier) {
                promises.push(getGeneralDepartmentPermitBySequenceIdentifier(props.sequenceIdentifier));
            } else if (props.copyOfDocumentReferenceId) {
                promises.push(getGeneralDepartmentPermitByDocumentId(props.copyOfDocumentReferenceId));
            }

            const [getDepartmentResult, getPermitResult] = await Promise.all(promises);

            if (getDepartmentResult.failed) {
                console.error(getDepartmentResult.error);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetDepartmentError"));
                setIsLoading(false);
                return;
            }

            if (getPermitResult && getPermitResult.failed) {
                console.error(getPermitResult.error);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetPermitError"));
                setIsLoading(false);
                return;
            }

            if (getPermitResult && props.departmentId !== getPermitResult.value.departmentId) {
                window.location.href = `/permits/generalDepartmentPermit/${getPermitResult.value.departmentId}?documentReferenceId=${getPermitResult.value.id}`;
                return;
            }

            if (getPermitResult && (props.documentReferenceId || props.sequenceIdentifier)) {
                setStoredPermit(getPermitResult.value);
                setFormState(toFormState(getPermitResult.value));
            } else if (getPermitResult) {
                //
                // getPermitResult.value is the permit with id copyOfDocumentReferenceId
                //
                setFormState(toFormState(getPermitResult.value));
            }

            setDepartment(getDepartmentResult.value);

            setIsLoading(false);
        }

        fetchData();
    }, []);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    function formStateChangeHandler(updatedProperty: keyof GeneralDepartmentPermitFormState, value: any) {
        setFormState((prevValue) => {
            const updatedFormElement = { ...prevValue[updatedProperty], value };

            return { ...prevValue, [updatedProperty]: updatedFormElement };
        });
    }

    function positionChangeHandler(updatedPosition: PositionPickerRow) {
        setFormState((prevValue) => {
            const updatedFormElement: PositionPickerRow[] = prevValue.positions.map((p) =>
                p.order === updatedPosition.order ? updatedPosition : p
            );

            const updatedFormState = { ...prevValue, positions: updatedFormElement };

            return updatedFormState;
        });
    }

    function addPositionHandler(newPosition: PositionPickerRow) {
        setFormState((prevValue) => {
            const updatedPositions = [
                ...prevValue.positions.map((p) => (p.order < newPosition.order ? p : { ...p, order: p.order + 1 })),
                newPosition
            ].sort((a, b) => a.order - b.order);

            const updatedFormState = { ...prevValue, positions: updatedPositions };

            return updatedFormState;
        });
    }

    function removePositionHandler(removedPosition: PositionPickerRow) {
        setFormState((prevValue) => {
            const updatedPositions = prevValue.positions
                .filter((p) => p.order !== removedPosition.order)
                .map((p) => (p.order < removedPosition.order ? p : { ...p, order: p.order - 1 }));

            const updatedFormState = { ...prevValue, positions: updatedPositions };

            return updatedFormState;
        });
    }

    function participantChangeHandler(updatedParticipant: ParticipantListItem) {
        setFormState((prevValue) => {
            const updatedParticipants: ParticipantListItem[] = prevValue.persons.map((p) =>
                p.index === updatedParticipant.index ? updatedParticipant : p
            );

            return {
                ...prevValue,
                persons: updatedParticipants
            };
        });
    }

    function addParticipantHandler(addedParticipant: ParticipantListItem) {
        setFormState((prevValue) => {
            const updatedParticipants: ParticipantListItem[] = [
                ...prevValue.persons.map((p) => (p.index < addedParticipant.index ? p : { ...p, index: p.index + 1 })),
                addedParticipant
            ].sort((a, b) => a.index - b.index);

            return {
                ...prevValue,
                persons: updatedParticipants
            };
        });
    }

    function removeParticipantHandler(removedParticipant: ParticipantListItem) {
        setFormState((prevValue) => {
            const updatedParticipants: ParticipantListItem[] = prevValue.persons
                .filter((p) => p.index !== removedParticipant.index)
                .map((p) => (p.index < removedParticipant.index ? p : { ...p, index: p.index - 1 }));

            return { ...prevValue, persons: updatedParticipants };
        });
    }

    function methodOfWorkChangeHandler(clickedOption: MethodOfWork) {
        setFormState((prevValue) => {
            let updatedMethodOfWork: MethodOfWork[] = [];

            if (prevValue.methodOfWork.value.includes(clickedOption)) {
                updatedMethodOfWork = prevValue.methodOfWork.value.filter((m) => m !== clickedOption);
            } else {
                updatedMethodOfWork = [...prevValue.methodOfWork.value, clickedOption];
            }

            return {
                ...prevValue,
                methodOfWork: { value: updatedMethodOfWork, errorMessage: prevValue.methodOfWork.errorMessage }
            };
        });
    }

    async function submitFormHandler(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (!storedPermit) {
            createPermit();
        } else {
            updatePermit();
        }

        scrollToTop();
    }

    async function createPermit() {
        const request: GeneralDepartmentPermitRequest = toCreateRequest(formState, props.departmentId);

        const createResult = await createGeneralDepartmentPermit(request);

        if (createResult.failed) {
            console.error(createResult);
            if (Object.keys(createResult.error.errors).length === 0) {
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("SendError"));
                return;
            }

            handleValidationErrors(createResult.error);
            return;
        }

        showSuccessAlert(props.resources.get("WorkPermitCreated"));

        const createdPermitDocumentId = createResult.value;

        const updatedUrl = `/permits/generalDepartmentPermit/${props.departmentId}?documentReferenceId=${createdPermitDocumentId}`;

        window.history.replaceState({ path: updatedUrl }, "", updatedUrl);
        const getCreatedPermitResult = await getGeneralDepartmentPermitByDocumentId(createdPermitDocumentId);

        if (getCreatedPermitResult.failed) {
            console.error(getCreatedPermitResult);
            window.location.reload();
            return;
        }

        const updatedFormState = toFormState(getCreatedPermitResult.value);

        setFormState(updatedFormState);
        setStoredPermit(getCreatedPermitResult.value);
    }

    async function updatePermit() {
        if (!storedPermit) {
            console.error("Attempted to update a permit with no stored permit in state.");
            return;
        }

        const request: GeneralDepartmentPermitRequest = toUpdateRequest(formState);

        const updateResult: Result<void, ProblemDetails> = await updateGeneralDepartmentPermit(
            storedPermit.id,
            request
        );
        if (updateResult.failed) {
            console.error(updateResult.error);
            if (Object.keys(updateResult.error.errors).length === 0) {
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("UpdateError"));
                return;
            }

            handleValidationErrors(updateResult.error);
            return;
        }

        showSuccessAlert(props.resources.get("WorkPermitUpdated"));

        const getCreatedPermitResult = await getGeneralDepartmentPermitByDocumentId(storedPermit.id);

        if (getCreatedPermitResult.failed) {
            console.error(getCreatedPermitResult);
            window.location.reload();
            return;
        }

        const updatedFormState = toFormState(getCreatedPermitResult.value);

        setFormState(updatedFormState);
        setStoredPermit(getCreatedPermitResult.value);
    }

    function handleValidationErrors(problemDetails: ProblemDetails) {
        const errorLocations = getErrorLocations(problemDetails, props.resources);
        showValidationErrorDialog(errorLocations, props.resources);

        const updatedFormState = populateErrorMessages(formState, problemDetails);

        setFormState(updatedFormState);
    }

    function startDateBlurHandler(e: React.FocusEvent<HTMLInputElement>) {
        const minValue = !storedPermit || storedPermit.version === 1 ? new Date() : new Date(storedPermit.startDate);

        let updatedStartDate = new Date(e.target.value);

        if (isNaN(updatedStartDate.getTime()) || updatedStartDate < minValue) {
            if (!startDateRef.current) {
                console.error("startDateRef is undefined.");
                return;
            }
            updatedStartDate = minValue;
            startDateRef.current.value = Date_toDateString(updatedStartDate);
        }

        updateStartEndDates(updatedStartDate, formState.endDate.value);
    }

    function endDateBlurHandler(e: React.FocusEvent<HTMLInputElement>) {
        let updatedEndDate = new Date(e.target.value);

        if (isNaN(updatedEndDate.getTime())) {
            if (!endDateRef.current) {
                console.error("endDateRef is undefined.");
                return;
            }

            updatedEndDate = new Date(formState.startDate.value);
            endDateRef.current.value = Date_toDateString(updatedEndDate);
        }

        updateStartEndDates(formState.startDate.value, updatedEndDate);
    }

    function updateStartEndDates(startDate: Date, endDate: Date) {
        if (endDate < startDate) {
            if (!endDateRef.current) {
                console.error("endDateRef is undefined.");
                return;
            }

            endDate = startDate;
            endDateRef.current.value = Date_toDateString(endDate);
        }

        setFormState((prevState) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevState,
                startDate: { ...prevState.startDate, value: startDate },
                endDate: { ...prevState.endDate, value: endDate }
            };

            return updatedFormState;
        });
    }

    function permitAgreementStartDateBlurHandler(e: React.FocusEvent<HTMLInputElement>) {
        let updatedStartDate = new Date(e.target.value);

        if (isNaN(updatedStartDate.getTime())) {
            if (!permitAgreementStartDateRef.current) {
                console.error("permitAgreementStartDateRef is undefined.");
                return;
            }

            updatedStartDate = new Date();
            permitAgreementStartDateRef.current.value = Date_toDateTimeString(updatedStartDate);
        }

        setFormState((prevState) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevState,
                permitAgreementStartDate: { ...prevState.permitAgreementStartDate, value: updatedStartDate }
            };

            return updatedFormState;
        });
    }

    function permitAgreementEndDateBlurHandler(e: React.FocusEvent<HTMLInputElement>) {
        let updatedEndDate = new Date(e.target.value);

        if (isNaN(updatedEndDate.getTime())) {
            if (!permitAgreementEndDateRef.current) {
                console.error("permitAgreementEndDateRef is undefined.");
                return;
            }

            updatedEndDate = formState.permitAgreementStartDate.value;
            permitAgreementEndDateRef.current.value = Date_toDateTimeString(updatedEndDate);
        }

        setFormState((prevState) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevState,
                permitAgreementEndDate: { ...prevState.permitAgreementEndDate, value: updatedEndDate }
            };

            return updatedFormState;
        });
    }

    async function permitAgreementProviderSignatureSaveHandler(signatureBlob: Blob, nameClarification: string) {
        await signatureSaveHandler(
            SignatureType.PermitAgreementPermitProvider,
            "permitAgreementProviderSignature",
            signatureBlob,
            nameClarification
        );
    }

    async function permitAgreementSeekerSignatureSaveHandler(signatureBlob: Blob, nameClarification: string) {
        await signatureSaveHandler(
            SignatureType.PermitAgreementPermitSeeker,
            "permitAgreementSeekerSignature",
            signatureBlob,
            nameClarification
        );
    }

    async function isFinishedPermitProviderSignatureSaveHandler(signatureBlob: Blob, nameClarification: string) {
        await signatureSaveHandler(
            SignatureType.IsFinishedPermitProvider,
            "isFinishedPermitProviderSignature",
            signatureBlob,
            nameClarification
        );

        if (formState.isFinishedPermitSeekerSignature.id) {
            await finishPermit();
        }
    }

    async function isFinishedPermitSeekerSignatureSaveHandler(signatureBlob: Blob, nameClarification: string) {
        await signatureSaveHandler(
            SignatureType.IsFinishedPermitSeeker,
            "isFinishedPermitSeekerSignature",
            signatureBlob,
            nameClarification
        );

        if (formState.isFinishedPermitProviderSignature.id) {
            await finishPermit();
        }
    }

    async function finishPermit() {
        const result = await finishGeneralDepartmentPermit(storedPermit?.entityId ?? 0);

        if (result.failed) {
            console.error(result.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("FinishError"));
            return;
        }

        showSuccessAlert(props.resources.get("PermitFinished"));
        await refreshStoredPermit();
    }

    async function acceptPermitClickHandler() {
        if (!storedPermit) {
            return;
        }

        if (storedPermit.permitAgreementStartDate < new Date()) {
            showDangerDialog(props.resources.get("CouldNotAccept"), props.resources.get("StartTimePassedAcceptError"));
            return;
        }

        const acceptResult: Result<void, ProblemDetails> = await acceptGeneralDepartmentPermit(storedPermit.entityId);

        if (acceptResult.error) {
            console.error(acceptResult);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("AcceptError"));
            return;
        }

        showSuccessAlert(props.resources.get("PermitAccepted"));

        await refreshStoredPermit();
    }

    async function refreshStoredPermit() {
        if (!storedPermit) {
            console.error("Tried to refresh stored permit with no stored permit in state.");
            return;
        }

        const storedPermitResult = await getGeneralDepartmentPermitByDocumentId(storedPermit.id);

        if (storedPermitResult.failed) {
            console.error(storedPermitResult.error);
            window.location.reload();
            return;
        }

        setStoredPermit(storedPermitResult.value);
    }

    async function copyPermitClickHandler() {
        if (!storedPermit) {
            return;
        }

        window.location.href = `/permits/generalDepartmentPermit/${props.departmentId}?CopyOfDocumentReferenceId=${storedPermit.id}`;
    }

    async function renewPermitClickHandler() {
        if (!storedPermit) {
            return;
        }

        const renewResult = await postRenewPermit(storedPermit.entityId);

        if (renewResult.failed) {
            console.error(renewResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RenewError"));
            return;
        }

        const updatedPermitResult = await getGeneralDepartmentPermitByDocumentId(storedPermit.id);

        if (updatedPermitResult.failed) {
            console.error(updatedPermitResult.error);
            window.location.reload();
            return;
        }

        setStoredPermit(updatedPermitResult.value);
    }

    async function checkoutPermitClickHandler() {
        if (!storedPermit) {
            return false;
        }

        const checkoutResult = await checkoutPermitAsync(storedPermit.entityId, PermitType.GeneralDepartmentPermit);
        if (checkoutResult.failed) {
            console.error(checkoutResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("CheckoutError"));
            return;
        }

        window.location.href = `/permits/generalDepartmentPermit/${storedPermit.departmentId}?SequenceIdentifier=${storedPermit.sequenceIdentifier}`;
    }

    async function updatePermitAgreementEndTimeClickHandler() {
        if (
            !storedPermit ||
            storedPermit.status !== CodeStatus.Accepted ||
            !storedPermit.canUpdatePermitAgreementEndDate
        ) {
            return;
        }

        const result = await updatePermitAgreementEndTimeOnAcceptedGeneralDepartmentPermit(
            storedPermit.id,
            formState.permitAgreementEndDate.value
        );

        if (result.error) {
            if (Object.keys(result.error.errors).length === 0) {
                console.error(result.error);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("UpdateError"));
                return;
            }

            handleValidationErrors(result.error);
            return;
        }

        showSuccessAlert(props.resources.get("WorkPermitUpdated"));
    }

    async function updatePositionActiveStatusHandler() {
        if (!storedPermit) {
            console.error("Tried to update position active status with no stored permit in state.");
            return;
        }

        const updateResult = await updateGeneralDepartmentPermitPositionActiveStatus(
            storedPermit.entityId,
            formState.positions.map(PositionPickerRow_toPosition)
        );

        if (updateResult.failed) {
            console.error(updateResult);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("UpdateError"));
            return;
        }

        showSuccessAlert(props.resources.get("PositionsUpdated"));
        setFormState((prevValue) => clearErrors(prevValue));
    }

    async function updateParticipantsClickHandler() {
        if (!storedPermit) {
            console.error("Tried to update the participants with no stored permit in state.");
            return;
        }

        const updateParticipantsRequest: UpdateParticipantsRequest = {
            participants: formState.persons.map(fromParticipantListItem),
            workLeaderIsActive: formState.workLeaderIsActive.value,
            projectLeaderIsActive: formState.projectLeaderIsActive.value
        };

        const result: Result<void, ProblemDetails> = await updateGeneralDepartmentPermitParticipants(
            storedPermit.entityId,
            updateParticipantsRequest
        );

        if (result.failed) {
            if (Object.keys(result.error.errors).length === 0) {
                console.error(result);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("UpdateError"));
                return;
            }

            handleValidationErrors(result.error);
            return;
        }

        showSuccessAlert(props.resources.get("ParticipantsUpdated"));
        setFormState((prevValue) => clearErrors(prevValue));
    }

    function isWorkFlammableChangeHandler(isFlammable: boolean) {
        setFormState((prevValue) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevValue,
                isWorkFlammable: { ...prevValue.isWorkFlammable, value: isFlammable }
            };

            if (isFlammable) {
                updatedFormState.permitCategory = {
                    ...prevValue.permitCategory,
                    value: PermitCategory.HotWorkCategoryOne
                };
            }

            return updatedFormState;
        });

        if (isFlammable && conditionsAccordionActiveKey !== hotWorkConditionsAccordionKey) {
            setConditionsAccordionActiveKey(hotWorkConditionsAccordionKey);
        }
    }

    function permitCategoryChangeHandler(newPermitCategory: PermitCategory) {
        formStateChangeHandler("permitCategory", newPermitCategory);

        if (newPermitCategory === PermitCategory.HotWorkCategoryOne) {
            setConditionsAccordionActiveKey(hotWorkConditionsAccordionKey);
        }
    }

    function measuresChangeHandler(updateMeasuresHandler: (prevMeasures: MeasuresFormState) => MeasuresFormState) {
        setFormState((prevValue) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevValue,
                measures: updateMeasuresHandler(prevValue.measures)
            };

            return updatedFormState;
        });
    }

    function protectiveEquipmentChangeHandler(
        checklistUpdateHandler: (prevValue: ProtectiveEquipmentFormState) => ProtectiveEquipmentFormState
    ) {
        setFormState((prevValue) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevValue,
                protectiveEquipment: checklistUpdateHandler(prevValue.protectiveEquipment)
            };

            return updatedFormState;
        });
    }

    function riskIdentificationsChangeHandler(
        checklistUpdateHandler: (prevValue: RiskIdentificationsFormState) => RiskIdentificationsFormState
    ) {
        setFormState((prevValue) => {
            const updatedFormState: GeneralDepartmentPermitFormState = {
                ...prevValue,
                riskIdentifications: checklistUpdateHandler(prevValue.riskIdentifications)
            };

            return updatedFormState;
        });
    }

    const permitAgreementEndDateMin = new Date(formState.permitAgreementStartDate.value);

    const permitAgreementEndDateMax: Date = new Date(formState.permitAgreementStartDate.value);
    permitAgreementEndDateMax.setDate(formState.permitAgreementStartDate.value.getDate() + 1);

    const permitAgreementStartDateMin = new Date();
    permitAgreementStartDateMin.setHours(0);
    permitAgreementStartDateMin.setMinutes(0);

    const permitAgreementStartDateMax = storedPermit ? new Date(storedPermit.endDate) : new Date();
    permitAgreementStartDateMax.setHours(23);
    permitAgreementStartDateMax.setMinutes(59);

    const isNewPermit = !storedPermit;

    const canAccept =
        department &&
        department.canAcceptPermits &&
        storedPermit &&
        storedPermit.status === CodeStatus.Registered &&
        storedPermit.permitAgreementStartDate.getDate() == new Date().getDate() &&
        storedPermit.permitAgreementStartDate > new Date() &&
        !!storedPermit.permitAgreementProviderSignature.id &&
        !!storedPermit.permitAgreementSeekerSignature.id;

    const canCheckout = storedPermit && storedPermit.canCheckout;

    const canCopy = storedPermit && department && department.canCreatePermits;

    const canRenew = storedPermit && storedPermit.canRenew;

    const canManageAttachments = storedPermit && storedPermit.canManageAttachments;

    const canManageContent = !storedPermit || storedPermit.canUpdate;

    const canUpdatePermitAgreementEndDate = !!storedPermit && storedPermit.canUpdatePermitAgreementEndDate;

    const canManagePositionPolygons =
        (!!isNewPermit &&
            props.baseUserInfo.roles.some(
                (r) =>
                    r.roleCode === CodeRole.Admin ||
                    r.roleCode === CodeRole.InternalCaseManager ||
                    r.roleCode === CodeRole.InternalDirectorFlammableProduct ||
                    r.roleCode === CodeRole.SystemOwner
            )) ||
        (!!storedPermit && storedPermit.canManagePositionPolygons);

    const showUpdatePermitAgreementEndDateButton =
        !!storedPermit && !storedPermit.canUpdate && canUpdatePermitAgreementEndDate;

    const canManageParticipants = isNewPermit || storedPermit.canUpdateParticipants;

    const showUpdateParticipantsButton =
        storedPermit && storedPermit.status === CodeStatus.Accepted && storedPermit.canUpdateParticipants;

    const showCannotAcceptStartTimeHasPassed =
        storedPermit &&
        storedPermit.status === CodeStatus.Registered &&
        storedPermit.permitAgreementStartDate < new Date() &&
        !!storedPermit.permitAgreementProviderSignature.id &&
        !!storedPermit.permitAgreementSeekerSignature.id;

    const showCannotAcceptStartDateInFuture =
        storedPermit &&
        storedPermit.status === CodeStatus.Registered &&
        storedPermit.permitAgreementStartDate.getDate() > new Date().getDate() &&
        !!storedPermit.permitAgreementProviderSignature.id &&
        !!storedPermit.permitAgreementSeekerSignature.id;

    const showUnderWaterWorkPermitIdMissing =
        formState.riskIdentifications.isRaftIncludedInPrint.value &&
        !formState.riskIdentifications.raftChecklist.underWaterWorkPermitSequenceIdentifier.value;

    const showDivingPermitIdMissing =
        formState.riskIdentifications.isDivingIncludedInPrint.value &&
        !formState.riskIdentifications.divingChecklist.divingPermitSequenceIdentifier.value;

    const canPrint = !!storedPermit;

    const canManagePositions = isNewPermit || storedPermit.canUpdate;
    const canManagePositionActiveStatus = isNewPermit || storedPermit.canUpdatePositionActiveStatus;

    const showUpdatePositionsButton =
        storedPermit && storedPermit.status === CodeStatus.Accepted && canManagePositionActiveStatus;

    const permitIcon = getPermitIcon(
        PermitType.GeneralDepartmentPermit,
        formState.permitCategory.value,
        storedPermit?.status ?? CodeStatus.New,
        false
    );

    return (
        <div className="mb-3">
            <div className="container">
                <div className="container mb-4 mt-4 d-flex align-items-center justify-content-center position-relative">
                    <div className="w-100 position-absolute">
                        {storedPermit && storedPermit.renewalOfPermitSequenceIdentifier ? (
                            <div className="mb-2">
                                <div>
                                    <span className="fw-bold me-1">{props.resources.get("RenewalOf")}:</span>
                                    <a
                                        href={`/Permits/GeneralDepartmentPermit/${props.departmentId}?SequenceIdentifier=${storedPermit.renewalOfPermitSequenceIdentifier}`}
                                    >
                                        {storedPermit.renewalOfPermitSequenceIdentifier}
                                    </a>
                                </div>
                                <div>
                                    <span className="fw-bold me-1">{props.resources.get("InitialStartDate")}:</span>
                                    <span>{storedPermit.initialStartDate.toLocaleDateString()}</span>
                                </div>
                            </div>
                        ) : null}
                        {storedPermit && storedPermit.renewedByPermitSequenceIdentifier ? (
                            <div className="mb-2">
                                <div>
                                    <span className="fw-bold me-1">{props.resources.get("RenewedBy")}:</span>
                                    <a
                                        href={`/Permits/GeneralDepartmentPermit/${props.departmentId}?SequenceIdentifier=${storedPermit.renewedByPermitSequenceIdentifier}`}
                                    >
                                        {storedPermit.renewedByPermitSequenceIdentifier}
                                    </a>
                                </div>
                                <div>
                                    <span className="fw-bold me-1">{props.resources.get("ExtendedEndDate")}:</span>
                                    <span>{storedPermit.extendedEndDate.toLocaleDateString()}</span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="d-flex justify-content-center align-items-center me-3 position-relative">
                        <img
                            src={permitIcon.markerSrc}
                            style={{width: "64px"}}
                            alt={`${props.resources.get("Permit")} ${!!department ? department.name : ""}`}
                        />
                    </div>
                    <div className="d-flex flex-column fw-bold">
                        <div className="mt-2">
                            {props.resources.get("Permit")} {!!department ? department.name : ""}
                        </div>
                        {storedPermit ? (
                            <>
                                <div>
                                    {props.resources.get("CreatedBy")}: {storedPermit.createdByUserName}
                                </div>
                                <div>
                                    {props.resources.get("Status")}: {props.resources.get(storedPermit.status)}
                                </div>
                                <div>
                                    {props.resources.get("Id")}: {storedPermit.sequenceIdentifier}-
                                    {storedPermit.version}
                                </div>
                                {storedPermit.isFinished ? props.resources.get("IsFinished") : null}
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="d-flex">
                    {canAccept ? (
                        <button
                            className="btn btn-success me-2"
                            title={props.resources.get("Accept")}
                            onClick={acceptPermitClickHandler}
                        >
                            {props.resources.get("Accept")}
                        </button>
                    ) : null}
                    {canCheckout ? (
                        <button className="btn btn-success ps-4 pe-4 me-2" onClick={checkoutPermitClickHandler}>
                            {props.resources.get("CheckOut")}
                        </button>
                    ) : null}
                    {canCopy ? (
                        <button
                            className="btn btn-secondary ps-4 pe-4 me-2"
                            title={props.resources.get("CopyOldToNew")}
                            onClick={copyPermitClickHandler}
                        >
                            <i className="fas fa-file"></i>
                        </button>
                    ) : null}
                    {canRenew ? (
                        <button
                            className="btn btn-secondary ps-4 pe-4 me-2"
                            title={props.resources.get("RenewPermit")}
                            onClick={renewPermitClickHandler}
                        >
                            <i className="fas fa-timer"></i>
                        </button>
                    ) : null}
                    {canPrint ? (
                        <a
                            className="btn btn-secondary ps-4 pe-4 me-2"
                            target="_blank"
                            href={`/api/workPermits/pdf/${storedPermit.entityId}`}
                            title={props.resources.get("Print")}
                        >
                            <i className="fas fa-print"></i>
                        </a>
                    ) : null}
                    {storedPermit ? (
                        <FilePicker
                            resources={props.resources}
                            storedPermit={storedPermit}
                            canManageAttachments={canManageAttachments ?? false}
                        />
                    ) : null}
                </div>

                {showCannotAcceptStartDateInFuture ? (
                    <div className="alert alert-info mt-2">
                        {props.resources.get("CanNotAccept_FutureStartTime")}
                        <span className="ms-1 me-1">{storedPermit.permitAgreementStartDate.toLocaleDateString()}.</span>
                        {props.resources.get("CanNotAccept_UpdateStartTimeBeforeAccepting")}
                    </div>
                ) : null}

                {showCannotAcceptStartTimeHasPassed ? (
                    <div className="alert alert-info mt-2">
                        {props.resources.get("CanNotAccept_StartTimeHasPassed")}
                    </div>
                ) : null}

                {showUnderWaterWorkPermitIdMissing ? (
                    <div className="alert alert-danger mt-3 text-center" style={{ fontSize: "18px" }}>
                        {props.resources.get("UnderWaterWorkPermitIdMissing")}
                    </div>
                ) : null}

                {showDivingPermitIdMissing ? (
                    <div className="alert alert-danger mt-3 text-center" style={{ fontSize: "18px" }}>
                        {props.resources.get("DivingPermitIdMissing")}
                    </div>
                ) : null}

                <form className=" mt-5" onSubmit={submitFormHandler}>
                    <PositionPicker
                        showUpdatePositionsButton={!!showUpdatePositionsButton}
                        positions={formState.positions}
                        canManagePositions={canManagePositions}
                        canManageComments={canManagePositions}
                        canManagePositionIsActive={canManagePositionActiveStatus}
                        canManagePolygons={canManagePositionPolygons}
                        onPositionChange={positionChangeHandler}
                        onPositionAdded={addPositionHandler}
                        onPositionRemoved={removePositionHandler}
                        onUpdatePositionsClicked={updatePositionActiveStatusHandler}
                        resources={props.positionPickerResources}
                        canManageCategories={false}
                        selectedCategories={[]}
                        showActiveStatus={true}
                        showCategories={false}
                    />
                    <div className="row">
                        <div className="form-group col-md-6">
                            <label className="form-label fw-bold" htmlFor="start-date">
                                {props.resources.get("StartDate")}
                            </label>
                            <input
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="date"
                                defaultValue={Date_toDateString(formState.startDate.value)}
                                ref={startDateRef}
                                readOnly={!props.canUpdateStartDate}
                                id="start-date"
                                aria-errormessage="start-date-error"
                                onBlur={startDateBlurHandler}
                            />
                            <span className="text-danger" id="start-date-error">
                                {formState.startDate.errorMessage}
                            </span>
                        </div>
                        <div className="form-group col-md-6">
                            <div className="form-group">
                                <label htmlFor="end-date" className="form-label fw-bold">
                                    {props.resources.get("EndDate")}
                                </label>
                                <input
                                    className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                    type="date"
                                    defaultValue={Date_toDateString(formState.endDate.value)}
                                    ref={endDateRef}
                                    id="end-date"
                                    aria-errormessage="end-date-error"
                                    onBlur={endDateBlurHandler}
                                    readOnly={!canManageContent}
                                />
                                <span id="end-date-error" className="text-danger">
                                    {formState.endDate.errorMessage}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="form-group col-md-3">
                            <label className="fw-bold">{props.resources.get("RemindMe")}</label>
                            <div>
                                <input
                                    className={`me-1 form-check-input ${!canManageContent ? "read-only-check" : ""}`}
                                    type="radio"
                                    value="true"
                                    onChange={() =>
                                        canManageContent ? formStateChangeHandler("remindMe", true) : false
                                    }
                                    checked={formState.remindMe.value}
                                    id="remind-me__yes"
                                />
                                <label htmlFor="remind-me__yes" className="me-2">
                                    {props.resources.get("Yes")}
                                </label>
                                <input
                                    className={`me-1 form-check-input ${!canManageContent ? "read-only-check" : ""}`}
                                    type="radio"
                                    value="false"
                                    onChange={() =>
                                        canManageContent ? formStateChangeHandler("remindMe", false) : false
                                    }
                                    checked={!formState.remindMe.value}
                                    id="remind-me__no"
                                />
                                <label htmlFor="remind-me__no" className="me-1">
                                    {props.resources.get("No")}
                                </label>
                            </div>
                        </div>
                        <div className="form-group col-md-3">
                            <label className="form-label fw-bold" htmlFor="expiration-reminder-email">
                                {props.resources.get("ExpirationReminderEmail")}
                            </label>
                            <input
                                id="expiration-reminder-email"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="email"
                                value={formState.expirationReminderEmail.value}
                                onChange={(e) => formStateChangeHandler("expirationReminderEmail", e.target.value)}
                                aria-errormessage="expiration-reminder-email-error"
                                readOnly={!canManageContent}
                            />
                            <span className="text-danger" id="expiration-reminder-email-error">
                                {formState.expirationReminderEmail.errorMessage}
                            </span>
                        </div>
                        <div className="form-group col-md-3">
                            <label className="form-label fw-bold" htmlFor="expiration-reminder-phone-number">
                                {props.resources.get("ExpirationReminderPhoneNumber")}
                            </label>
                            <input
                                id="expiration-reminder-phone-number"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="tel"
                                placeholder="e.g. +46705555555"
                                value={formState.expirationReminderPhoneNumber.value}
                                onChange={(e) =>
                                    formStateChangeHandler("expirationReminderPhoneNumber", e.target.value)
                                }
                                aria-errormessage="expiration-reminder-phone-number-error"
                                readOnly={!canManageContent}
                            />
                            <span className="text-danger" id="expiration-reminder-phone-number-error">
                                {formState.expirationReminderPhoneNumber.errorMessage}
                            </span>
                        </div>
                        <div className="form-group col-md-3">
                            <label className="form-label fw-bold" htmlFor="remind-me-date">
                                {props.resources.get("RemindMeDate")}
                            </label>
                            <input
                                id="remind-me-date"
                                className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                type="date"
                                defaultValue={Date_toDateString(formState.remindMeDate.value)}
                                onBlur={(e) => formStateChangeHandler("remindMeDate", new Date(e.target.value))}
                                aria-errormessage="remind-me-date-error"
                                readOnly={!canManageContent}
                            />
                            <span id="remind-me-date-error" className="text-danger">
                                {formState.remindMeDate.errorMessage}
                            </span>
                        </div>
                    </div>
                    <div className="mt-3">
                        <div className="d-flex">
                            <label className="fw-bold me-1">{props.resources.get("TypeOfWork")}:</label>
                            <RadioButton
                                checked={formState.permitCategory.value === PermitCategory.ColdWork}
                                inputId="type-of-work__cold-work"
                                label={props.resources.get("ColdWork")}
                                name="type-of-work"
                                onSelect={() => permitCategoryChangeHandler(PermitCategory.ColdWork)}
                                readOnly={!canManageContent}
                                containerClassName="me-3"
                            />
                            <RadioButton
                                checked={formState.permitCategory.value === PermitCategory.HotWorkCategoryOne}
                                inputId="type-of-work__hot-work"
                                label={props.resources.get("HotWork")}
                                name="type-of-work"
                                onSelect={() => permitCategoryChangeHandler(PermitCategory.HotWorkCategoryOne)}
                                readOnly={!canManageContent}
                                containerClassName="me-3"
                            />
                        </div>
                    </div>

                    <TextInput
                        containerClassName="col-md-6 mt-2"
                        inputId="entrepreneur"
                        labelText={props.resources.get("Entrepreneur")}
                        onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                        errorMessage={formState.entrepreneur.errorMessage}
                        readOnly={!canManageContent}
                        value={formState.entrepreneur.value}
                    />

                    <div className="d-flex mt-3">
                        <label className="fw-bold me-2">{props.resources.get("IsWorkFlammable")}</label>
                        <RadioButton
                            checked={formState.isWorkFlammable.value}
                            onSelect={() => isWorkFlammableChangeHandler(true)}
                            inputId="is-work-flammable__yes"
                            label={props.resources.get("Yes")}
                            name="is-work-flammable"
                            readOnly={!canManageContent}
                            containerClassName="me-3"
                        />
                        <RadioButton
                            checked={!formState.isWorkFlammable.value}
                            onSelect={() => isWorkFlammableChangeHandler(false)}
                            inputId="is-work-flammable__no"
                            label={props.resources.get("No")}
                            name="is-work-flammable"
                            readOnly={!canManageContent}
                        />
                    </div>

                    <TextArea
                        errorMessage={formState.description.errorMessage}
                        labelText={props.resources.get("Description")}
                        value={formState.description.value}
                        onChange={(s) => formStateChangeHandler("description", s)}
                        readOnly={!canManageContent}
                        textAreaId="description"
                        containerClassName="mt-2"
                    />

                    <div className="row mt-3">
                        {showUpdateParticipantsButton ? (
                            <div>
                                <button
                                    className="btn btn-primary mt-2 mb-2"
                                    onClick={updateParticipantsClickHandler}
                                    type="button"
                                >
                                    {props.resources.get("UpdateParticipants")}
                                </button>
                            </div>
                        ) : null}

                        <ColumnCheckbox
                            checked={formState.workLeaderIsActive.value}
                            label={props.resources.get("Active")}
                            onChange={(b) => formStateChangeHandler("workLeaderIsActive", b)}
                            inputId="work-leader__is-active"
                            containerClassName="col-1 d-flex flex-column align-items-center"
                            readOnly={!canManageParticipants}
                        />

                        <TextInput
                            inputId="work-leader__name"
                            containerClassName="col-md-5"
                            value={formState.workLeaderName.value}
                            errorMessage={formState.workLeaderName.errorMessage}
                            labelText={props.resources.get("WorkLeader")}
                            onChange={(s) => formStateChangeHandler("workLeaderName", s)}
                            readOnly={!canManageContent}
                            inputClassName={formState.workLeaderIsActive.value ? "border border-2 border-success" : ""}
                        />
                        <TextInput
                            inputId="work-leader__phone-number"
                            containerClassName="col-md-5"
                            value={formState.workLeaderPhoneNumber.value}
                            errorMessage={formState.workLeaderPhoneNumber.errorMessage}
                            labelText={props.resources.get("PhoneNumber")}
                            placeHolder="e.g. +46705555555"
                            onChange={(s) => formStateChangeHandler("workLeaderPhoneNumber", s)}
                            readOnly={!canManageContent}
                            inputClassName={formState.workLeaderIsActive.value ? "border border-2 border-success" : ""}
                        />
                    </div>
                    <ParticipantsList
                        participantListItems={formState.persons}
                        onParticipantChange={participantChangeHandler}
                        onAddParticipant={addParticipantHandler}
                        onRemoveParticipant={removeParticipantHandler}
                        resources={props.resources}
                        readOnly={!canManageParticipants}
                    />

                    <div className="row mt-3">
                        <ColumnCheckbox
                            checked={formState.projectLeaderIsActive.value}
                            label={props.resources.get("Active")}
                            onChange={(b) => formStateChangeHandler("projectLeaderIsActive", b)}
                            inputId="project-leader__is-active"
                            containerClassName="col-1 d-flex flex-column align-items-center"
                            readOnly={!canManageParticipants}
                        />

                        <TextInput
                            inputId="project-leader__name"
                            containerClassName="col-md-5"
                            value={formState.projectLeaderName.value}
                            errorMessage={formState.projectLeaderName.errorMessage}
                            labelText={props.resources.get("ProjectLeader")}
                            onChange={(s) => formStateChangeHandler("projectLeaderName", s)}
                            readOnly={!canManageContent}
                            inputClassName={
                                formState.projectLeaderIsActive.value ? "border border-2 border-success" : ""
                            }
                        />

                        <TextInput
                            inputId="project-leader__phone-number"
                            containerClassName="col-md-5"
                            value={formState.projectLeaderPhoneNumber.value}
                            errorMessage={formState.projectLeaderPhoneNumber.errorMessage}
                            labelText={props.resources.get("PhoneNumber")}
                            placeHolder="e.g. +46705555555"
                            onChange={(s) => formStateChangeHandler("projectLeaderPhoneNumber", s)}
                            readOnly={!canManageContent}
                            inputClassName={
                                formState.projectLeaderIsActive.value ? "border border-2 border-success" : ""
                            }
                        />
                    </div>

                    <Accordion className="mt-3" alwaysOpen>
                        <Accordion.Item eventKey={workConditionsAccordionKey}>
                            <Accordion.Header>
                                <span className="fw-bold">{props.resources.get("WorkTerms")}</span>
                            </Accordion.Header>
                            <Accordion.Body>
                                <div className="row">
                                    <div className="col-4 p-0">
                                        <ProtectiveEquipment
                                            formState={formState.protectiveEquipment}
                                            onChange={protectiveEquipmentChangeHandler}
                                            resources={props.resources}
                                            canManageContent={canManageContent}
                                        />
                                    </div>
                                    <div className="col-4 p-0">
                                        <Measures
                                            formState={formState.measures}
                                            onChange={measuresChangeHandler}
                                            resources={props.resources}
                                            uiState={measuresUIState}
                                            permitType={PermitType.GeneralDepartmentPermit}
                                            canManageContent={canManageContent}
                                            dbMeasures={storedPermit?.measures}
                                        />
                                    </div>
                                    <div className="col-4 p-0">
                                        <RiskIdentifications
                                            formState={formState.riskIdentifications}
                                            onChange={riskIdentificationsChangeHandler}
                                            resources={props.resources}
                                            uiState={riskIdentificationsUIState}
                                            canCreateDivingPermits={props.canCreateDivingPermits}
                                            canCreateUnderWaterWorkPermits={props.canCreateUnderWaterWorkPermits}
                                            canManageContent={canManageContent}
                                            dbRiskIdentifications={storedPermit?.riskIdentifications}
                                        />
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                        {formState.permitCategory.value === PermitCategory.HotWorkCategoryOne ? (
                            <Accordion.Item eventKey={hotWorkConditionsAccordionKey}>
                                <Accordion.Header>
                                    <span className="fw-bold">{props.resources.get("HotWorkConditions")}</span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="mt-3 mb-2">
                                        <div className="d-flex flex-wrap">
                                            <label className="fw-bold me-1">
                                                {props.resources.get("MethodOfWork")}:
                                            </label>

                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.Welding)}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.Welding)}
                                                label={props.resources.get("Welding")}
                                                id="method-of-work__welding"
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />
                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(
                                                    MethodOfWork.GrindingDisc
                                                )}
                                                id="method-of-work__grinding-disc"
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.GrindingDisc)}
                                                label={props.resources.get("GrindingDisc")}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />
                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.Cutting)}
                                                id="method-of-work_cutting"
                                                label={props.resources.get("Cutting")}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.Cutting)}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />
                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.Soldering)}
                                                id="method-of-work__soldering"
                                                label={props.resources.get("Soldering")}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.Soldering)}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />
                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.HotAir)}
                                                id="method-of-work__hot-air"
                                                label={props.resources.get("HotAir")}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.HotAir)}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />

                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.AsphaltPot)}
                                                id="method-of-work__asphalt-pot"
                                                label={props.resources.get("AsphaltPot")}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.AsphaltPot)}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />

                                            <LabelCheckbox
                                                checked={formState.methodOfWork.value.includes(MethodOfWork.TigerSaw)}
                                                id="method-of-work__tiger-saw"
                                                label={props.resources.get("TigerSaw")}
                                                onChange={() => methodOfWorkChangeHandler(MethodOfWork.TigerSaw)}
                                                readOnly={!canManageContent}
                                                containerClassName="me-3"
                                            />

                                            <div className="d-flex">
                                                <div className="text-nowrap">
                                                    <LabelCheckbox
                                                        checked={formState.methodOfWork.value.includes(
                                                            MethodOfWork.Other
                                                        )}
                                                        id="method-of-work__other"
                                                        label={props.resources.get("Other")}
                                                        onChange={() => methodOfWorkChangeHandler(MethodOfWork.Other)}
                                                        readOnly={!canManageContent}
                                                        containerClassName="me-3"
                                                    />
                                                </div>
                                                {formState.methodOfWork.value.includes(MethodOfWork.Other) ? (
                                                    <TextInput
                                                        style={{ height: "2rem", minWidth: "20rem", marginTop: -2 }}
                                                        inputId="other-method-of-work"
                                                        value={formState.otherMethodOfWork.value}
                                                        onChange={(s) => formStateChangeHandler("otherMethodOfWork", s)}
                                                        labelText=""
                                                        placeHolder={props.resources.get("ProvideOtherMethodOfWork")}
                                                        readOnly={!canManageContent}
                                                    />
                                                ) : null}
                                            </div>
                                        </div>
                                        <span className="text-danger">{formState.methodOfWork.errorMessage}</span>
                                    </div>
                                    <LabelCheckbox
                                        checked={formState.isAuthorizedForHotWork.value}
                                        id="is-authorized-for-hot-work"
                                        label={props.resources.get("IsAuthorizedForHotWork")}
                                        onChange={(b) => formStateChangeHandler("isAuthorizedForHotWork", b)}
                                        readOnly={!canManageContent}
                                        containerClassName="mb-3"
                                    />
                                    <LabelCheckbox
                                        checked={formState.surveillanceByFireWatch.value}
                                        id="surveillance-by-fire-watch"
                                        label={props.resources.get("SurveillanceByFireWatch")}
                                        onChange={(b) => formStateChangeHandler("surveillanceByFireWatch", b)}
                                        readOnly={!canManageContent}
                                    />

                                    <TextInput
                                        inputId="fire-watch-name"
                                        labelText={props.resources.get("FireWatchName")}
                                        style={{ height: "2rem" }}
                                        onChange={(s) => formStateChangeHandler("fireWatchName", s)}
                                        readOnly={!canManageContent}
                                        value={formState.fireWatchName.value}
                                        containerClassName="ps-5 d-flex align-items-center"
                                        boldLabel={false}
                                        labelClassName="text-nowrap me-2"
                                    />

                                    <LabelCheckbox
                                        containerClassName="ps-5 mb-3"
                                        id="fire-watch-not-needed"
                                        label={props.resources.get("FireWatchNotNeeded")}
                                        onChange={(s) => formStateChangeHandler("fireWatchNotNeeded", s)}
                                        checked={formState.fireWatchNotNeeded.value}
                                        readOnly={!canManageContent}
                                    />

                                    <LabelCheckbox
                                        checked={formState.postWorkSurveillanceArranged.value}
                                        id="post-work-surveillance-arranged"
                                        label={props.resources.get("PostWorkSurveillanceArranged")}
                                        onChange={(b) => formStateChangeHandler("postWorkSurveillanceArranged", b)}
                                        readOnly={!canManageContent}
                                    />

                                    <TextInput
                                        inputId="post-work-surveillance-fire-watch"
                                        labelText={props.resources.get("PostWorkSurveillanceFireWatch")}
                                        onChange={(s) => formStateChangeHandler("postWorkSurveillanceFireWatch", s)}
                                        readOnly={!canManageContent}
                                        value={formState.postWorkSurveillanceFireWatch.value}
                                        containerClassName="ps-5 d-flex align-items-center"
                                        style={{ height: "2rem" }}
                                        boldLabel={false}
                                    />

                                    <div className="ps-5 mb-3">
                                        <label htmlFor="post-work-surveillance-hours" className="me-1">
                                            {props.resources.get("PostWorkSurveillance")}
                                        </label>
                                        <input
                                            id="post-work-surveillance-hours"
                                            className={`form-control d-inline me-1 ${
                                                !canManageContent ? "read-only-input" : ""
                                            }`}
                                            style={{ width: "4rem", height: "2rem" }}
                                            type="number"
                                            readOnly={!canManageContent}
                                            min={1}
                                            step={1}
                                            value={formState.postWorkSurveillanceHours.value}
                                            onChange={(e) =>
                                                formStateChangeHandler(
                                                    "postWorkSurveillanceHours",
                                                    e.target.value === ""
                                                        ? e.target.value
                                                        : isNaN(Number.parseInt(e.target.value))
                                                          ? formState.postWorkSurveillanceHours.value
                                                          : Number.parseInt(e.target.value)
                                                )
                                            }
                                        />
                                        <span className="me-5">h</span>
                                    </div>

                                    <LabelCheckbox
                                        checked={formState.forWorkWithFlammableGoods.value}
                                        id="for-work-with-flammable-goods"
                                        label={props.resources.get("ForWorkWithFlammableGoods")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("forWorkWithFlammableGoods", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.workplaceCleaned.value}
                                        id="workplace-cleaned"
                                        label={props.resources.get("WorkplaceCleaned")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("workplaceCleaned", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.combustibleMaterialsRemoved.value}
                                        id="combustible-materials-removed"
                                        label={props.resources.get("CombustibleMaterialsRemoved")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("combustibleMaterialsRemoved", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.inAreaWithExplosiveAtmosphere.value}
                                        id="in-area-with-explosive-atmosphere"
                                        label={props.resources.get("InAreaWithExplosiveAtmosphere")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("inAreaWithExplosiveAtmosphere", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.openings.value}
                                        id="openings"
                                        label={props.resources.get("Openings")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("openings", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.extinguishingEquipment.value}
                                        id="extinguishing-equipment"
                                        label={props.resources.get("ExtinguishingEquipment")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("extinguishingEquipment", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.weldingEquipment.value}
                                        id="welding-equipment"
                                        label={props.resources.get("WeldingEquipment")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("weldingEquipment", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.weldingEquipmentFaultless.value}
                                        id="welding-equipment-faultless"
                                        label={props.resources.get("WeldingEquipmentFaultless")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("weldingEquipmentFaultless", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.emergencyServicesCanBeCalled.value}
                                        id="emergency-services-can-be-called"
                                        label={props.resources.get("EmergencyServicesCanBeCalled")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("emergencyServicesCanBeCalled", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.waterproofing.value}
                                        id="waterproofing"
                                        label={props.resources.get("Waterproofing")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("waterproofing", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.openFlamesForMeltingIce.value}
                                        id="open-flames-for-melting-ice"
                                        label={props.resources.get("OpenFlamesForMeltingIce")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("openFlamesForMeltingIce", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.maximumHeat.value}
                                        id="maximum-heat"
                                        label={props.resources.get("MaximumHeat")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("maximumHeat", b)}
                                        containerClassName="mb-3"
                                    />

                                    <LabelCheckbox
                                        checked={formState.whenMeltingAsphalt.value}
                                        id="when-melting-asphalt"
                                        label={props.resources.get("WhenMeltingAsphalt")}
                                        readOnly={!canManageContent}
                                        onChange={(b) => formStateChangeHandler("whenMeltingAsphalt", b)}
                                        containerClassName="mb-3"
                                    />

                                    <TextArea
                                        errorMessage={formState.followingSections.errorMessage}
                                        labelText={props.resources.get("FollowingSections")}
                                        onChange={(s) => formStateChangeHandler("followingSections", s)}
                                        readOnly={!canManageContent}
                                        textAreaId="following-sections"
                                        value={formState.followingSections.value}
                                        boldLabel={false}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        ) : null}
                    </Accordion>
                    {storedPermit ? (
                        <div className="text-bg-light mt-3 rounded p-3">
                            <h5 className="mb-0">{props.resources.get("PermitAgreementHeader")}</h5>
                            <div className="mb-2">{props.resources.get("SafetyRulesConsidered")}</div>
                            {showUpdatePermitAgreementEndDateButton ? (
                                <button
                                    type="button"
                                    className="btn btn-primary mt-3 mb-2"
                                    onClick={updatePermitAgreementEndTimeClickHandler}
                                >
                                    {props.resources.get("UpdateEndTime")}
                                </button>
                            ) : null}
                            <div className="row mb-2">
                                <div className="form-group col-md-6">
                                    <label className="form-label fw-bold" htmlFor="permit-agreement-start-date">
                                        {props.resources.get("WorkBeginsAt")}
                                    </label>
                                    <input
                                        id="permit-agreement-start-date"
                                        aria-errormessage="permit-agreement-start-date-error"
                                        className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                                        type="datetime-local"
                                        defaultValue={Date_toDateTimeString(
                                            formState.permitAgreementStartDate.value ?? new Date()
                                        )}
                                        onBlur={permitAgreementStartDateBlurHandler}
                                        ref={permitAgreementStartDateRef}
                                        min={Date_toDateTimeString(permitAgreementStartDateMin)}
                                        max={Date_toDateTimeString(permitAgreementStartDateMax)}
                                        readOnly={!canManageContent}
                                    />
                                    <span className="text-danger" id="permit-agreement-start-date-error">
                                        {formState.permitAgreementStartDate.errorMessage}
                                    </span>
                                </div>
                                <div className="form-group col-md-6">
                                    <label className="form-label fw-bold" htmlFor="permit-agreement-end-date">
                                        {props.resources.get("WorkEndsAt")}
                                    </label>
                                    <input
                                        id="permit-agreement-end-date"
                                        aria-errormessage="permit-agreement-end-date-error"
                                        className={`form-control ${
                                            !canUpdatePermitAgreementEndDate ? "read-only-input" : ""
                                        }`}
                                        type="datetime-local"
                                        defaultValue={Date_toDateTimeString(
                                            formState.permitAgreementEndDate.value ?? new Date()
                                        )}
                                        onBlur={permitAgreementEndDateBlurHandler}
                                        ref={permitAgreementEndDateRef}
                                        min={Date_toDateTimeString(permitAgreementEndDateMin)}
                                        max={Date_toDateTimeString(permitAgreementEndDateMax)}
                                        readOnly={!canUpdatePermitAgreementEndDate}
                                    />
                                    <span className="text-danger" id="permit-agreement-end-date-error">
                                        {formState.permitAgreementEndDate.errorMessage}
                                    </span>
                                </div>
                            </div>

                            <div className="d-flex">
                                <div className="me-5">
                                    <SignatureDisplay
                                        resources={props.resources}
                                        signatureLabel={props.resources.get(SignatureType.PermitAgreementPermitSeeker)}
                                        signatureManager={permitAgreementSeekerSignatureManager}
                                    />
                                </div>
                                <div className="ms-5">
                                    <SignatureDisplay
                                        resources={props.resources}
                                        signatureLabel={props.resources.get(
                                            SignatureType.PermitAgreementPermitProvider
                                        )}
                                        signatureManager={permitAgreementProviderSignatureManager}
                                    />
                                </div>
                            </div>
                            <div className="d-flex mt-3">
                                <TextInput
                                    inputId="permit-agreement-fire-watch"
                                    labelText={props.resources.get("FireGuard")}
                                    value={formState.permitAgreementFireWatch.value}
                                    onChange={(s) => formStateChangeHandler("permitAgreementFireWatch", s)}
                                    errorMessage={formState.permitAgreementFireWatch.errorMessage}
                                    readOnly={!canManageContent}
                                    containerClassName="me-4 w-50"
                                />

                                <TextInput
                                    inputId="permit-agreement-way-of-communication"
                                    labelText={props.resources.get("WayOfCommunication")}
                                    onChange={(s) => formStateChangeHandler("permitAgreementWayOfCommunication", s)}
                                    readOnly={!canManageContent}
                                    value={formState.permitAgreementWayOfCommunication.value}
                                    containerClassName="w-50"
                                    errorMessage={formState.permitAgreementWayOfCommunication.errorMessage}
                                />
                            </div>
                        </div>
                    ) : null}
                    {storedPermit &&
                    storedPermit.status !== CodeStatus.New &&
                    storedPermit.status !== CodeStatus.Registered ? (
                        <Accordion className="mt-3">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    <span className="fw-bold">{props.resources.get("FinishPermit")}</span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {storedPermit.canViewFinishPermitLink ? (
                                        <div className="alert alert-info">
                                            {props.resources.get("FinishPermitLinkInfo")}
                                            <a
                                                className="ms-1"
                                                href={`/permits/generalDepartmentPermit/${storedPermit.departmentId}?DocumentReferenceId=${storedPermit.lastInSeriesDocumentId}`}
                                            >
                                                {props.resources.get("FinishPermitLinkText")}
                                            </a>
                                        </div>
                                    ) : null}
                                    <>
                                        <div className="mb-3">{props.resources.get("WorkIsFinished")}</div>
                                        <div className="d-flex">
                                            <SignatureDisplay
                                                resources={props.resources}
                                                signatureLabel={props.resources.get(
                                                    SignatureType.IsFinishedPermitSeeker
                                                )}
                                                signatureManager={isFinishedPermitSeekerSignatureManager}
                                                containerClassName="me-4"
                                                showDate
                                            />
                                            <SignatureDisplay
                                                resources={props.resources}
                                                signatureLabel={props.resources.get(
                                                    SignatureType.IsFinishedPermitProvider
                                                )}
                                                signatureManager={isFinishedPermitProviderSignatureManager}
                                                showDate
                                            />
                                        </div>
                                    </>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    ) : null}
                    {storedPermit && storedPermit.canUpdate ? (
                        <button type="submit" className="btn btn-primary mt-3">
                            {props.resources.get("Update")}
                        </button>
                    ) : !storedPermit ? (
                        <button type="submit" className="btn btn-primary mt-3">
                            {props.resources.get("Send")}
                        </button>
                    ) : null}
                </form>
            </div>
            <SignatureModal
                resources={props.resources}
                SignatureManager={permitAgreementProviderSignatureManager}
                ref={permitAgreementProviderSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={props.resources}
                SignatureManager={permitAgreementSeekerSignatureManager}
                ref={permitAgreementSeekerSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={props.resources}
                SignatureManager={isFinishedPermitProviderSignatureManager}
                ref={isFinishedPermitProviderSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={props.resources}
                SignatureManager={isFinishedPermitSeekerSignatureManager}
                ref={isFinishedPermitSeekerSignatureManager.signaturePadRef}
            />
        </div>
    );
}

export default GeneralDepartmentPermitForm;

function getErrorLocations(problemDetails: ProblemDetails, resources: Resources): string[] {
    const errorLocations: string[] = [];

    const errorKeys = Object.keys(problemDetails.errors);

    errorKeys.forEach((errorKey) => {
        let location = "";

        if (errorKey.includes("Positions")) {
            location = "Positions";
        } else if (
            (errorKey.includes("StartDate") || errorKey.includes("EndDate")) &&
            !errorKey.includes("PermitAgreement")
        ) {
            location = "WorkDuration";
        } else if (errorKey.includes("Entrepreneur")) {
            location = "Entrepreneur";
        } else if (errorKey.includes("Description")) {
            location = "Description";
        } else if (errorKey.includes("WorkLeader")) {
            location = "WorkLeader";
        } else if (errorKey.includes("Persons")) {
            location = "Participants";
        } else if (errorKey.includes("ProjectLeader")) {
            location = "ProjectLeader";
        } else if (errorKey.includes("PermitAgreement")) {
            location = "PermitAgreement";
        } else if (errorKey.includes("MethodOfWork")) {
            location = "MethodOfWork";
        } else if (errorKey.includes("Measures")) {
            location = "WorkTerms";
        }

        if (!location) {
            return;
        }

        const locationResource = resources.get(location) ?? "";

        errorLocations.push(locationResource);
    });

    const uniqueLocations = Array.from(new Set(errorLocations));

    return uniqueLocations;
}
